import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { type Item, ITEM_FIELDS, RenderVariant, SegmentationTargets } from '../../../../../types';
import {
	type UpdateFormFieldForAllItemsType,
	type UpdateMainFormFieldType,
} from '../../../../../ui/items-shared/types';
import { ConfigFieldDropdown, type FieldOption } from '../../config-field-dropdown';
import type { ItemPlusSegmentationParameters } from '../types';

import { messages } from './messages';

const defaultOption = {
	label: messages.segmentationNoneDropDownItemLabel,
	value: undefined,
};

const manualSegmentationTargetOptions: FieldOption<SegmentationTargets | undefined>[] = [
	defaultOption,
	{
		label: messages.segmentationAllDropDownItemLabel,
		value: SegmentationTargets.ALL,
	},
	{
		label: messages.segmentationEachDropDownItemLabel,
		value: SegmentationTargets.EACH,
	},
];

const dynamicSegmentationTargetOptions: FieldOption<SegmentationTargets | undefined>[] = [
	defaultOption,
	{
		label: messages.segmentationAllDropDownItemLabel,
		value: SegmentationTargets.ALL,
	},
];

type SegmentationTargetFieldProps = {
	targetParameter: SegmentationTargets | undefined;
	updateMainFormField: UpdateMainFormFieldType<ItemPlusSegmentationParameters>;
	updateCardFormFieldForAllCards: UpdateFormFieldForAllItemsType<Item>;
	renderVariant: RenderVariant;
	audienceSegmentationGroupIds: string[] | undefined;
	source: string;
};

export const SegmentationTargetFieldV2 = ({
	targetParameter,
	updateMainFormField,
	updateCardFormFieldForAllCards,
	renderVariant,
	audienceSegmentationGroupIds,
	source,
}: SegmentationTargetFieldProps) => {
	const { formatMessage } = useIntl();

	const displayedSegmentationTargetOptions =
		renderVariant === RenderVariant.MANUAL
			? manualSegmentationTargetOptions
			: dynamicSegmentationTargetOptions;

	const selectedOption =
		displayedSegmentationTargetOptions.find(({ value }) => value === targetParameter) ||
		defaultOption;

	const handleSelect = useCallback(
		(selectedLabel: FieldOption<SegmentationTargets | undefined>) => {
			if (fg('company-hub-dynamic-cards-audience-segmentation')) {
				const fieldsToUpdate: ItemPlusSegmentationParameters = {
					segmentationTarget: selectedLabel.value,
					isSegmentationShown: selectedLabel.value !== undefined,
				};
				if (selectedLabel.value === undefined) {
					//clear group preferences when set to none
					fieldsToUpdate.audienceSegmentationGroupIds = undefined;
					updateCardFormFieldForAllCards({
						[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: undefined,
					});
				} else if (
					selectedLabel.value === SegmentationTargets.ALL &&
					targetParameter === SegmentationTargets.EACH
				) {
					//clear each group preferences when set to all from each
					updateCardFormFieldForAllCards({
						[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: undefined,
					});
				} else if (
					selectedLabel.value === SegmentationTargets.EACH &&
					targetParameter === SegmentationTargets.ALL
				) {
					//transfer all group preferences to each when set to each from all
					fieldsToUpdate.audienceSegmentationGroupIds = undefined;
					updateCardFormFieldForAllCards({
						[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: audienceSegmentationGroupIds,
					});
				}
				updateMainFormField(fieldsToUpdate);
			} else {
				updateMainFormField({
					segmentationTarget: selectedLabel.value,
					isSegmentationShown: selectedLabel.value !== undefined,
				});
				if (selectedLabel.value !== SegmentationTargets.EACH) {
					updateCardFormFieldForAllCards({
						[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: undefined,
					});
				}
			}
		},
		[
			updateMainFormField,
			updateCardFormFieldForAllCards,
			targetParameter,
			audienceSegmentationGroupIds,
		],
	);

	return (
		<ConfigFieldDropdown<SegmentationTargets | undefined>
			fieldType="segmentationTarget"
			label={messages.segmentationTargetLabel}
			onSelect={handleSelect}
			options={displayedSegmentationTargetOptions}
			selectedOption={selectedOption}
			renderVariant={renderVariant}
			infoTooltip={formatMessage(messages.infoIconLabelText)}
			showUpdatedStyles
			source={source}
		/>
	);
};
